import {
  type InvalidateOptions,
  type InvalidateQueryFilters,
  type QueryKey,
  useQueryClient,
} from "@tanstack/react-query";
import type { Middleware, MutationHook } from "react-query-kit";

type GuardFunctionOnSuccess<TData, TVariables, TContext> = (
  data: TData,
  variables: TVariables,
  context: TContext,
) => boolean;

type GuardFunctionOnSettled<TData, TError, TVariables, TContext> = (
  data: TData | undefined,
  error: TError | null,
  variables: TVariables,
  context: TContext | undefined,
) => boolean;

interface FiltersInvalidateEntry {
  type: "filters";
  queryFilters: InvalidateQueryFilters;
}

interface GuardInvalidateEntryOnSuccess<TData, TVariables, TContext> {
  type: "guard";
  queryFilters: InvalidateQueryFilters;
  guard: GuardFunctionOnSuccess<TData, TVariables, TContext>;
}

interface GuardInvalidateEntryOnSettled<TData, TError, TVariables, TContext> {
  type: "guard";
  queryFilters: InvalidateQueryFilters;
  guard: GuardFunctionOnSettled<TData, TError, TVariables, TContext>;
}

type InvalidateEntryOnSuccess<TData, TVariables, TContext> =
  | FiltersInvalidateEntry
  | GuardInvalidateEntryOnSuccess<TData, TVariables, TContext>;

type InvalidateEntryOnSettled<TData, TError, TVariables, TContext> =
  | FiltersInvalidateEntry
  | GuardInvalidateEntryOnSettled<TData, TError, TVariables, TContext>;

type InvalidateEntriesOnSuccess<TData, TVariables, TContext> = (
  | QueryKey
  | InvalidateEntryOnSuccess<TData, TVariables, TContext>
  | (() => QueryKey)
)[];

type InvalidateEntriesOnSettled<TData, TError, TVariables, TContext> = (
  | QueryKey
  | InvalidateEntryOnSettled<TData, TError, TVariables, TContext>
  | (() => QueryKey)
)[];

function invalidateQueriesAfterMutation<TData, TVariables, TError, TContext>(
  entries: InvalidateEntriesOnSuccess<TData, TVariables, TContext>,
  options: {
    method?: "onSuccess";
    invalidateOptions?: InvalidateOptions;
    waitForInvalidation?: boolean;
  },
): Middleware<MutationHook<TData, TVariables, TError, TContext>>;

function invalidateQueriesAfterMutation<TData, TVariables, TError, TContext>(
  entries: InvalidateEntriesOnSettled<TData, TError, TVariables, TContext>,
  options?: {
    method?: "onSettled";
    invalidateOptions?: InvalidateOptions;
    waitForInvalidation?: boolean;
  },
): Middleware<MutationHook<TData, TVariables, TError, TContext>>;

/**
 * Creates a middleware that automatically invalidates specified queries after a mutation based on mutation outcome.
 * This middleware can be configured to invalidate queries on either success or settled states of the mutation.
 *
 * @template TData - The data type returned by the mutation
 * @template TVariables - The variables type for the mutation
 * @template TError - The error type for the mutation
 * @template TContext - The context type for the mutation
 *
 * @param entries - The entries describing what queries to invalidate
 *   - Can be an array of query keys
 *   - A function returning a query key
 *   - An object with a 'filters' type containing query filters
 *   - An object with a 'guard' type containing a predicate function and query filters
 *
 * @param options - Configuration options
 * @param options.method - When to invalidate queries: 'onSuccess' (default) or 'onSettled'
 * @param options.invalidateOptions - Options to pass to queryClient.invalidateQueries
 * @param options.waitForInvalidation - Whether to wait for query invalidation to complete before calling the original callbacks
 *
 * @returns A React Query mutation middleware that automatically invalidates the specified queries
 */
function invalidateQueriesAfterMutation<TData, TVariables, TError, TContext>(
  entries:
    | InvalidateEntriesOnSuccess<TData, TVariables, TContext>
    | InvalidateEntriesOnSettled<TData, TError, TVariables, TContext>,
  options?: {
    method?: "onSuccess" | "onSettled";
    invalidateOptions?: InvalidateOptions;
    waitForInvalidation?: boolean;
  },
): Middleware<MutationHook<TData, TVariables, TError, TContext>> {
  return (useMutationNext) => {
    return (mutationOptions) => {
      const queryClient = useQueryClient();

      const method = options?.method ?? "onSettled";
      const shouldWaitForInvalidation = options?.waitForInvalidation ?? false;

      if (method === "onSuccess") {
        const onSuccess = async (
          data: TData,
          variables: TVariables,
          context: TContext,
        ) => {
          const invalidationPromises: Promise<void>[] = [];

          for (const entry of entries as InvalidateEntriesOnSuccess<
            TData,
            TVariables,
            TContext
          >) {
            if (Array.isArray(entry)) {
              // Entry is QueryKey
              const promise = queryClient.invalidateQueries(
                { queryKey: entry },
                options?.invalidateOptions,
              );

              invalidationPromises.push(promise);
            } else if (typeof entry === "function") {
              // Entry is a function which returns a QueryKey
              const promise = queryClient.invalidateQueries(
                { queryKey: entry() },
                options?.invalidateOptions,
              );

              invalidationPromises.push(promise);
            } else {
              const keyEntry = entry as InvalidateEntryOnSuccess<
                TData,
                TVariables,
                TContext
              >;

              // Entry is InvalidateEntryOnSuccess
              if (keyEntry.type === "filters") {
                const promise = queryClient.invalidateQueries(
                  keyEntry.queryFilters,
                  options?.invalidateOptions,
                );

                invalidationPromises.push(promise);
              } else if (keyEntry.type === "guard") {
                if (keyEntry.guard(data, variables, context)) {
                  const promise = queryClient.invalidateQueries(
                    keyEntry.queryFilters,
                    options?.invalidateOptions,
                  );

                  invalidationPromises.push(promise);
                }
              }
            }
          }

          if (shouldWaitForInvalidation) {
            await Promise.all(invalidationPromises);
          }

          const originalOnSuccess = mutationOptions.onSuccess;
          const result = originalOnSuccess?.(data, variables, context);

          if (shouldWaitForInvalidation && result instanceof Promise) {
            await result;
          }
        };

        // biome-ignore lint/correctness/useHookAtTopLevel: <explanation>
        return useMutationNext({ ...mutationOptions, onSuccess });
      }

      if (method === "onSettled") {
        const onSettled = async (
          data: TData | undefined,
          error: TError | null,
          variables: TVariables,
          context: TContext | undefined,
        ) => {
          const invalidationPromises: Promise<void>[] = [];

          for (const entry of entries as InvalidateEntriesOnSettled<
            TData,
            TError,
            TVariables,
            TContext
          >) {
            if (Array.isArray(entry)) {
              // Entry is QueryKey
              const promise = queryClient.invalidateQueries(
                { queryKey: entry },
                options?.invalidateOptions,
              );

              invalidationPromises.push(promise);
            } else if (typeof entry === "function") {
              // Entry is a function which returns a QueryKey
              const promise = queryClient.invalidateQueries(
                { queryKey: entry() },
                options?.invalidateOptions,
              );

              invalidationPromises.push(promise);
            } else {
              const keyEntry = entry as InvalidateEntryOnSettled<
                TData,
                TError,
                TVariables,
                TContext
              >;

              // Entry is InvalidateEntryOnSettled
              if (keyEntry.type === "filters") {
                const promise = queryClient.invalidateQueries(
                  keyEntry.queryFilters,
                  options?.invalidateOptions,
                );

                invalidationPromises.push(promise);
              } else if (keyEntry.type === "guard") {
                if (keyEntry.guard(data, error, variables, context)) {
                  const promise = queryClient.invalidateQueries(
                    keyEntry.queryFilters,
                    options?.invalidateOptions,
                  );

                  invalidationPromises.push(promise);
                }
              }
            }
          }

          if (shouldWaitForInvalidation) {
            await Promise.all(invalidationPromises);
          }

          const originalOnSettled = mutationOptions.onSettled;
          const result = originalOnSettled?.(data, error, variables, context);

          if (shouldWaitForInvalidation && result instanceof Promise) {
            await result;
          }
        };

        // biome-ignore lint/correctness/useHookAtTopLevel: <explanation>
        return useMutationNext({ ...mutationOptions, onSettled });
      }

      // biome-ignore lint/correctness/useHookAtTopLevel: <explanation>
      return useMutationNext(mutationOptions);
    };
  };
}

export { invalidateQueriesAfterMutation };
